import { useCallback, useEffect, useState } from 'react';
import useQuery from 'hooks/react-query/useQuery';
import useQueryClient from 'hooks/react-query/useClientQuery';

import usePusher from '../usePusher';
import getRepoDocuments from 'api/v2/repository/getRepoDocuments';
import { DocumentsRepositoryCacheKeys } from 'types/cacheKeys.types';
import type { DocumentItem } from 'types/repository.types';
import { useIsBEFinFeatureEnabled } from 'utils/docsRepo/useIsBEFinFeatureEnabled/useIsBEFinFeatureEnabled';

interface PusherData {
  data: DocumentItem;
  role: string;
  eventId: string;
  source: string;
}
const useUnopenedDocsUI = () => {
  const isFeatureEnabled = useIsBEFinFeatureEnabled();

  const channel = usePusher();
  const queryClient = useQueryClient();
  const [shouldFetch, setShouldFetch] = useState(false);

  const unopenedDocsQuery = useQuery(
    [DocumentsRepositoryCacheKeys.UnopenedDocuments],
    () =>
      getRepoDocuments({
        page: 1,
        perPage: 1,
        opened: false,
      }),
    {
      enabled: shouldFetch && isFeatureEnabled,
      staleTime: 0,
      onSuccess: () => {
        setShouldFetch(false);
      },
    },
  );
  const unopenedDocsCount = unopenedDocsQuery.data?.paging?.totalCount || 0;

  useEffect(() => {
    if (!channel) return;

    const handler = (_data: PusherData) => {
      setShouldFetch(true);
    };

    channel.bind('repository-document.created', handler);

    return () => {
      channel.unbind('repository-document.created', handler);
    };
  }, [channel]);

  const resetUnopenedDocs = useCallback(() => {
    queryClient.invalidateQueries([
      DocumentsRepositoryCacheKeys.UnopenedDocuments,
    ]);
    setShouldFetch(true);
  }, [queryClient]);

  return { resetUnopenedDocs, unopenedDocsCount };
};

export default useUnopenedDocsUI;
